/**
 * Lightweight analytics helper — POSTs events to /api/analytics/track.
 * Safe: never throws, silently drops on any error so it can't break the UI.
 * Session ID is stored in sessionStorage (unique per tab/browser session).
 */
import { metaTrack } from './metaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SID_KEY = 'mitzva_analytics_sid';

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SID_KEY);
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return `s_${Date.now().toString(36)}`;
  }
};

export const trackEvent = (event, extra = {}) => {
  try {
    if (window.gtag) window.gtag('event', event, extra);
    else if (window.dataLayer) window.dataLayer.push({ event, ...extra });
  } catch (e) {
    if (process.env.NODE_ENV === 'development') console.debug('gtag/dataLayer mirror failed:', e);
  }
  try {
    // eslint-disable-next-line no-use-before-define
    metaTrack(event, extra);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') console.debug('meta pixel mirror failed:', e);
  }
  try {
    const body = {
      event,
      session_id: getSessionId(),
      path: typeof window !== 'undefined' ? window.location.pathname + window.location.search : null,
      referrer: typeof document !== 'undefined' ? document.referrer : null,
      ...extra,
    };
    // sendBeacon is fire-and-forget and survives page unload — perfect for analytics
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon(`${API_URL}/api/analytics/track`, blob);
    } else {
      fetch(`${API_URL}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) {
    // Analytics must never break the UI — log only in development
    if (process.env.NODE_ENV === 'development') console.debug('analytics track failed:', e);
  }
};

export const trackPageView = () => trackEvent('page_view');
export const trackProductView = (productId) => trackEvent('product_view', { product_id: productId });
export const trackShare = (productId, channel) => trackEvent('share', { product_id: productId, channel });
export const trackAddToCart = (productId) => trackEvent('add_to_cart', { product_id: productId });
export const trackCheckoutStart = () => trackEvent('checkout_start');
export const trackPurchase = (value) => trackEvent('purchase', { value });
